import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../store';

export type SnackbarSeverity = 'success' | 'info' | 'warning' | 'error';

export type SnackbarState = Readonly<{
  message: string;
  severity: SnackbarSeverity;
}>;

export type SignupPromptContext = 'general' | 'blueprint' | 'sat_act_booster' | 'sandbox';

interface AppState {
  isMaintenanceMode: boolean;
  pageTitle: string | null;
  signupPromptOpen: boolean;
  signupPromptContext: SignupPromptContext;
  gettingStartedOpen: boolean;
  hasNewVersion: boolean;
  snackbar: SnackbarState | null;
}

const initialState: AppState = {
  isMaintenanceMode: false,
  pageTitle: null,
  signupPromptOpen: false,
  signupPromptContext: 'general',
  gettingStartedOpen: false,
  hasNewVersion: false,
  snackbar: null,
};

export const appStateSlice = createSlice({
  name: 'appState',
  initialState,
  reducers: {
    setMaintenanceMode: (state, action: PayloadAction<boolean>) => {
      if (typeof action.payload === 'boolean') {
        state.isMaintenanceMode = action.payload;
      } else {
        console.warn('setMaintenanceMode received non-boolean payload:', action.payload);
      }
    },
    setPageTitle: (state, action: PayloadAction<string | null>) => {
      state.pageTitle = action.payload;
    },
    openSignupPrompt: {
      reducer: (state, action: PayloadAction<SignupPromptContext | undefined>) => {
        state.signupPromptOpen = true;
        state.signupPromptContext = action.payload ?? 'general';
      },
      prepare: (context?: SignupPromptContext) => ({ payload: context }),
    },
    closeSignupPrompt: (state) => {
      state.signupPromptOpen = false;
    },
    openGettingStarted: (state) => {
      state.gettingStartedOpen = true;
    },
    closeGettingStarted: (state) => {
      state.gettingStartedOpen = false;
    },
    setHasNewVersion: (state, action: PayloadAction<boolean>) => {
      state.hasNewVersion = action.payload;
    },
    // Single-slot: replaces any currently-displayed snackbar. There is no queue,
    // so a second showSnackbar while one is visible drops the earlier message.
    showSnackbar: (state, action: PayloadAction<SnackbarState>) => {
      state.snackbar = action.payload;
    },
    dismissSnackbar: (state) => {
      state.snackbar = null;
    },
  },
});

export const {
  setMaintenanceMode,
  setPageTitle,
  openSignupPrompt,
  closeSignupPrompt,
  openGettingStarted,
  closeGettingStarted,
  setHasNewVersion,
  showSnackbar,
  dismissSnackbar,
} = appStateSlice.actions;

export const selectIsMaintenanceMode = (state: RootState): boolean =>
  state?.appState?.isMaintenanceMode ?? false;

export const selectPageTitle = (state: RootState): string | null =>
  state?.appState?.pageTitle ?? null;

export const selectSignupPromptOpen = (state: RootState): boolean =>
  state?.appState?.signupPromptOpen ?? false;

export const selectSignupPromptContext = (state: RootState): SignupPromptContext =>
  state?.appState?.signupPromptContext ?? 'general';

export const selectGettingStartedOpen = (state: RootState): boolean =>
  state?.appState?.gettingStartedOpen ?? false;

export const selectHasNewVersion = (state: RootState): boolean =>
  state?.appState?.hasNewVersion ?? false;

export const selectSnackbar = (state: RootState): SnackbarState | null =>
  state?.appState?.snackbar ?? null;

export const appStateReducer = appStateSlice.reducer;
